import { STORAGE_ZUSTAND_KEYS } from "@/modules/core/store"
import { LoggerType } from "@/modules/utils/loggerUtils"
import { useEffect } from "react"

/**
 * Reads the persisted `isAuthenticated` flag out of a Zustand-persist localStorage payload
 * (shape: `{ state: { isAuthenticated, user }, version }`).
 * Returns false when the value is absent or unparseable — i.e. treated as logged out.
 */
export const readPersistedIsAuthenticated = (rawValue: string | null): boolean => {
  if (!rawValue) return false
  try {
    const parsed = JSON.parse(rawValue) as { state?: { isAuthenticated?: boolean } }
    return parsed.state?.isAuthenticated === true
  } catch {
    return false
  }
}

/**
 * True when the authentication state flipped between two persisted payloads (login or logout).
 * Plain profile updates rewrite the same key without flipping `isAuthenticated`, so they
 * return false and are ignored.
 */
export const isCrossTabAuthTransition = (oldValue: string | null, newValue: string | null): boolean =>
  readPersistedIsAuthenticated(oldValue) !== readPersistedIsAuthenticated(newValue)

/**
 * Keeps authentication in sync across browser tabs.
 *
 * The auth state is persisted to localStorage by the app-state store. localStorage mutations
 * fire a `storage` event in *other* tabs (never the originating one), so when the user logs in
 * or out — including the forced logout after repeated refresh-token failures — we detect the
 * `isAuthenticated` transition here and reload the tab.
 *
 * Reloading re-bootstraps the tab from the now-consistent localStorage (Cognito tokens +
 * persisted auth state), which avoids any partial in-memory state (stale React Query cache,
 * Zustand stores, navigation) and lands the tab on the correct screen.
 */
export const useCrossTabAuthSync = () => {
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_ZUSTAND_KEYS.USER_AUTHENTICATION) return
      if (!isCrossTabAuthTransition(event.oldValue, event.newValue)) return

      const isAuthenticated = readPersistedIsAuthenticated(event.newValue)
      console.log(
        LoggerType.Auth +
          `[CrossTab] authentication changed in another tab (authenticated: ${String(isAuthenticated)}) — reloading 🔄`,
      )
      window.location.reload()
    }

    window.addEventListener("storage", onStorage)
    return () => {
      window.removeEventListener("storage", onStorage)
    }
  }, [])
}
